import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import classNames from "classnames";

import { usePlanzonePermissions } from "@components/permissions";
import {
  useProjectEdition,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveStrategicAxes,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { Assignable, ProjectListingConfiguration } from "@planzone/dto";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { useModal } from "@root/client/modal";
import { FolderIcon } from "@common/folderIcon";
import { WeatherReport } from "@common/weatherReport";
import { WeightTag } from "@common/weightTag";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { ZoneAvatar } from "@common/zoneAvatar";
import { AllocationUnit } from "@common/allocationUnit";
import { RestrictedArea } from "@scenes/restrictedArea";
import { ProjectStatus } from "@scenes/project/projectStatus";
import { ProjectPopup, ToggleIcon } from "@scenes/projectPopup";
import { Safeguard } from "@scenes/safeguard";
import { TagList } from "./tagList";
import { ProjectPortfoliosList } from "./portfolioList";
import { ResourceList } from "./managerList";
import { toFixed } from "../utils";
import { Props } from "./types";
import styles from "./styles.scss";

function Project({
  project,
  profile,
  colNbr,
  areProjectStatsHiddenInProjectListing,
  getColSize,
}: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { strategicAxes } = useRetrieveStrategicAxes();
  const { isAdministrator } = usePlanzonePermissions();
  const { isRequestPending, editProject } = useProjectEdition(project);
  const { pushView } = useModal();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const fixedColMap = {
    zone: () => (
      <div className={styles.dates}>
        {project.zone && (
          <div className={styles.zone}>
            <div className={styles.avatar}>
              <ZoneAvatar name={project.zone.name} color={project.zone.color} />
            </div>
            <div className={styles.infoBubble}>
              <p>{project.zone.name}</p>
            </div>
          </div>
        )}
      </div>
    ),
    startDate: () => (
      <div className={styles.dates}>
        <p className={styles.date}>
          {project.rootList?.startDate
            ? new Date(project.rootList.startDate).toLocaleDateString()
            : "-"}
        </p>
      </div>
    ),
    endDate: () => (
      <div className={styles.dates}>
        <p className={styles.date}>
          {project.rootList?.endDate
            ? new Date(project.rootList.endDate).toLocaleDateString()
            : "-"}
        </p>
      </div>
    ),
    portfolios: () => (
      <div className={styles.portfolios}>
        <ProjectPortfoliosList
          project={project}
          isRemovable
          remove={(portfolio) =>
            pushView({
              key: UseCases.removeProjectFromPortfolio,
              data: { portfolio, project },
            })
          }
        />
      </div>
    ),
    tags: () => (
      <div className={styles.tags}>
        <TagList
          tags={project.tags}
          project={project}
          type={Assignable.PROJECT}
          id={project.uuid}
        />
      </div>
    ),
    status: () => (
      <div className={styles.status}>
        <ProjectStatus
          project={project}
          selectStatus={(status) =>
            !isRequestPending &&
            editProject({
              project,
              nextProject: { ...project, status },
            })
          }
          isShortened
        />
      </div>
    ),
    weather: () => (
      <div className={styles.weather}>
        <WeatherReport
          weather={project.attributes.weather}
          className={styles.weatherIcon}
        />
      </div>
    ),
    strategicScore: () => (
      <Safeguard useCases={[UseCases.createProjectStrategicAxis]}>
        {({ canCreateProjectStrategicAxis }) => (
          <div
            className={classNames(styles.weather, {
              [styles.createProjectStrategicAxis]:
                canCreateProjectStrategicAxis,
            })}
            onClick={() =>
              canCreateProjectStrategicAxis &&
              pushView({
                data: project,
                key: UseCases.createProjectStrategicAxis,
              })
            }
          >
            <WeightTag
              weight={project.averageOfConsistencyOfStrategicAxes}
              maxWeight={strategicAxes.reduce(
                (acc, { weight }) => acc + weight * 2,
                0,
              )}
            />
          </div>
        )}
      </Safeguard>
    ),
    activityMonitoring_done: () => (
      <p className={classNames(styles.text, styles.positive)}>
        {toFixed(project.proportionOfAccomplishedWork)}%
      </p>
    ),
    activityMonitoring_delayed: () => (
      <p className={classNames(styles.text, styles.warning)}>
        {toFixed(project.proportionOfDelayedWork)}%
      </p>
    ),
    activityMonitoring_opened: () => (
      <p className={styles.text}>{toFixed(project.proportionOfOpenedWork)}%</p>
    ),
    workload_allocatedTime: () => (
      <p
        className={classNames(styles.text, {
          [styles.warning]: project.totalSumOfTimeAllocatedOnTheProject < 0,
        })}
      >
        {toFixed(project.totalSumOfTimeAllocatedOnTheProject)}{" "}
        <AllocationUnit />
      </p>
    ),
    workload_spentTime: () => (
      <p
        className={classNames(styles.text, {
          [styles.warning]: project.totalOfTimesSpentInProject < 0,
        })}
      >
        {toFixed(project.totalOfTimesSpentInProject)} <AllocationUnit />
      </p>
    ),
    workload_remainingWork: () => (
      <p
        className={classNames(styles.text, {
          [styles.warning]: project.totalOfRemainingTimeInProject < 0,
        })}
      >
        {toFixed(project.totalOfRemainingTimeInProject)} <AllocationUnit />
      </p>
    ),
    budget_plannedBudget: () => (
      <p className={styles.text}>
        {toFixed(project.finalPlannedValueOfProject)}€
      </p>
    ),
    budget_realCosts: () => (
      <p
        className={classNames(styles.text, {
          [styles.warning]:
            project.trackingRealCost > project.finalPlannedValueOfProject,
        })}
      >
        {toFixed(project.trackingRealCost)}€
      </p>
    ),
    budget_acquiredValue: () => (
      <p className={styles.text}>{toFixed(project.trackingAcquiredValue)}€</p>
    ),
    budget_allocatedBudget: () => (
      <p className={styles.text}>{toFixed(project.allocatedBudget)}€</p>
    ),
    budget_realMargin: () => (
      <p
        className={classNames(styles.text, {
          [styles.warning]: project.realMarginBudget < 0,
          [styles.positive]: project.realMarginBudget > 0,
        })}
      >
        {toFixed(project.realMarginBudget)}€
      </p>
    ),
    budget_plannedMargin: () => (
      <p
        className={classNames(styles.text, {
          [styles.warning]: project.plannedMarginBudget < 0,
          [styles.positive]: project.plannedMarginBudget > 0,
        })}
      >
        {toFixed(project.plannedMarginBudget)}€
      </p>
    ),
    projectManagers: () => (
      <div className={styles.tags}>
        <ResourceList resources={project.projectManagers} />
      </div>
    ),
  };

  return (
    <RestrictedArea areas={["strategicAxes"]}>
      {({ canAccessStrategicAxes }) => (
        <Safeguard useCases={[UseCases.editProject]} context={{ project }}>
          {({ canEditProject }) => (
            <div
              className={classNames(styles.project, {
                [styles.admin]: isAdministrator,
                [styles.hiddenStats]: areProjectStatsHiddenInProjectListing,
                [styles.withStrategicAxes]: canAccessStrategicAxes,
              })}
            >
              <div
                className={styles.row}
                // style={{ minWidth: "20%", width: 0 }}
                // style={{ minWidth: "28rem", width: 0 }}
                style={{ minWidth: getColSize("name"), width: 0 }}
              >
                <FolderIcon className={styles.icon} />
                <Link to={`/projects/${project.uuid}`} className={styles.link}>
                  <h2 className={styles.name}>{project.name}</h2>
                </Link>
                {canEditProject && (
                  <VisibilitySwitch
                    renderChildren={(isVisible, show, hide) => (
                      <div
                        className={styles.projectPopup}
                        onMouseEnter={show}
                        onMouseLeave={hide}
                      >
                        <ToggleIcon className={styles.popupIcon} />
                        {isVisible && (
                          <div className={styles.projectPopupMain}>
                            <ProjectPopup projectId={project.uuid} />
                          </div>
                        )}
                      </div>
                    )}
                  />
                )}
                <div
                  className={classNames(styles.contextualMenu, {
                    [styles.addMargin]: !canEditProject,
                  })}
                >
                  <SecureContextualMenu
                    items={[
                      {
                        name: t("projectSection.contextualMenu.projectDetails"),
                        useCase: UseCases.editProject,
                        act: () =>
                          pushView({
                            data: { project },
                            key: UseCases.retrieveProject,
                          }),
                      },
                      {
                        name: t(
                          "projectSection.contextualMenu.createProjectModel",
                        ),
                        useCase: UseCases.createProjectModelFromProject,
                        act: () =>
                          pushView({
                            data: project,
                            key: UseCases.createProjectModelFromProject,
                          }),
                      },
                      {
                        name: t("projectSection.contextualMenu.projectStatus"),
                        useCase: UseCases.editProject,
                        act: () =>
                          pushView({
                            data: project,
                            key: UseCases.retrieveTaskStatuses,
                          }),
                      },
                      {
                        name: t(
                          "projectSection.contextualMenu.strategicImpact",
                        ),
                        useCase: UseCases.createProjectStrategicAxis,
                        act: () =>
                          pushView({
                            data: project,
                            key: UseCases.createProjectStrategicAxis,
                          }),
                      },
                      {
                        name: t("projectSection.contextualMenu.move"),
                        useCase: UseCases.transferProjects,
                        act: () =>
                          pushView({
                            data: { zone: project.zone, projects: [project] },
                            key: UseCases.transferProjects,
                          }),
                      },
                      {
                        name: t("projectSection.contextualMenu.duplicate"),
                        useCase: UseCases.duplicateProject,
                        act: () =>
                          pushView({
                            data: { zone: project.zone, project },
                            key: UseCases.duplicateProject,
                          }),
                      },
                      {
                        name: t("projectSection.contextualMenu.remove"),
                        useCase: UseCases.removeProject,
                        color: "#DB5F63FF",
                        act: () =>
                          pushView({
                            data: project,
                            key: UseCases.removeProject,
                          }),
                      },
                    ]}
                  />
                </div>
              </div>
              {profile.elements
                .reduce<ProjectListingConfiguration[number]["elements"]>(
                  (elements, element) => [
                    ...elements,
                    ...(element.type === "section"
                      ? element.elements
                      : [element]),
                  ],
                  [],
                )
                .map((element) => {
                  const criteria = template.find(
                    ({ uuid }) => uuid === element.key,
                  );

                  return (
                    <div
                      key={element.key}
                      className={classNames(styles.col, styles[element.key], {
                        [styles.criteriaCol]: !!criteria,
                      })}
                      // style={{ minWidth: `${80 / colNbr}%`, width: 0 }}
                      // style={{ minWidth: `10rem`, width: 0 }}
                      style={{ minWidth: getColSize(element.key), width: 0 }}
                    >
                      {!!criteria ? (
                        <SecondaryCriteriaForm
                          criteria={criteria}
                          value={
                            project.attributes
                              ? project.attributes[criteria.uuid] || ""
                              : ""
                          }
                          flexible
                          disabled={isRequestPending}
                          submit={(value) =>
                            editProject({
                              project,
                              nextProject: {
                                ...project,
                                attributes: {
                                  ...project.attributes,
                                  [criteria.uuid]: value,
                                },
                              },
                            })
                          }
                        />
                      ) : !!fixedColMap[
                          element.key as keyof typeof fixedColMap
                        ] ? (
                        fixedColMap[element.key as keyof typeof fixedColMap]()
                      ) : (
                        t(
                          `projectSection.columnNames.${element.key}`,
                          element.key,
                        )
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </Safeguard>
      )}
    </RestrictedArea>
  );
}

export { Project };
